
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button3D } from '../ui/button-3d';
import { Search, Plus } from 'lucide-react';
import { ResponsiveContainer } from '../layout/ResponsiveContainer';

interface HeroSectionProps {
  heroImages: string[];
  currentImageIndex: number;
}

const HeroSection: React.FC<HeroSectionProps> = ({ heroImages, currentImageIndex }) => {
  const navigate = useNavigate();

  return (
    <section 
      className="relative min-h-screen overflow-hidden"
      role="banner"
      aria-label="Hero section"
    >
      {/* Single Background Image - Optimized for LCP */}
      <img
        src="/lovable-uploads/23932d17-c28c-4482-8879-227b3a353a91.png"
        alt=""
        width="1813"
        height="1311"
        className="absolute inset-0 z-0 w-full h-full object-cover object-center"
        fetchPriority="high"
        loading="eager"
        aria-hidden="true"
      />

      {/* Hero Content - Premium Tech-Forward Design */}
      <ResponsiveContainer size="xl" className="relative z-10 pt-20 sm:pt-24 md:pt-28">
        <div className="text-center text-white animate-fade-in-subtle min-h-[calc(100vh-5rem)] flex flex-col justify-start">
          {/* Top Section - Premium Heading */}
          <div>
            {/* Main Heading with Enhanced Tech Styling */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-12 drop-shadow-2xl leading-none tracking-tight">
              <span 
                className="bg-gradient-to-r from-pink-300 via-pink-400 to-pink-500 bg-clip-text text-transparent"
                style={{
                  background: 'linear-gradient(45deg, #f9a8d4, #f472b6, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Find Your Match
              </span>
            </h1>

            {/* Premium Subtitle */}
            <p className="text-xl sm:text-2xl text-white max-w-4xl mx-auto mb-12 leading-relaxed font-light">
              Tell Fiona (our AI brain) what you're looking for, and we'll find your best possible rental match, in seconds.
            </p>

            {/* CTA Buttons - Moved up below subtitle */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
              <Button3D 
                size="xl" 
                variant="primary" 
                onClick={() => navigate('/signup')}
                className="transform hover:scale-105 transition-all duration-300 w-full sm:w-auto shadow-2xl"
                aria-label="Find your perfect rental match today"
              >
                <Search className="w-5 h-5 mr-2" aria-hidden="true" />
                Find My Match
              </Button3D>
              
              <Button3D 
                size="xl" 
                variant="secondary" 
                onClick={() => navigate('/signup')}
                className="transform hover:scale-105 transition-all duration-300 w-full sm:w-auto shadow-2xl"
                aria-label="List your rental property"
              >
                <Plus className="w-5 h-5 mr-2" aria-hidden="true" />
                List A Rental
              </Button3D>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    </section>
  );
};

export default HeroSection;
